import moment from 'moment';
import { Action, Module, Mutation } from 'vuex-module-decorators';
import {
  AccountingDocument,
  AccountingDocumentStatusEnum,
  AccountingDocumentTypeEnum,
} from '@gid/models';
import {
  CrudList,
  CrudListActionsEnum,
  CrudListFetchPayload,
  CrudListFilters,
} from '@gid/vue-common/store/shared/crud-list.module';
import Api from '@gid/vue-common/api';
import { CondOperator } from '@dataui/crud-request';

export enum _AccountingDocumentsActionsEnum {}
// Add job view specific actions/mutations here

export const AccountingDocumentsActionsEnum = {
  ...CrudListActionsEnum,
  ..._AccountingDocumentsActionsEnum,
};

const ACTIVE_CANCELLATION_STATUSES: AccountingDocumentStatusEnum[] = [
  AccountingDocumentStatusEnum.CREATED,
  AccountingDocumentStatusEnum.SEND_PENDING,
  AccountingDocumentStatusEnum.SENT,
  AccountingDocumentStatusEnum.PAID,
];

export function calculateTotal(docs: AccountingDocument[]) {
  return docs.reduce((acc, doc) => {
    const isWrittenOffOrCancelled =
      doc.status == AccountingDocumentStatusEnum.WRITTEN_OFF ||
      (doc.cancellationStatus &&
        ACTIVE_CANCELLATION_STATUSES.includes(doc.cancellationStatus))
        ? 0
        : 1;

    return (
      acc +
      doc.totalAmount *
        (doc.type == AccountingDocumentTypeEnum.CREDIT_NOTE ? -1 : 1) *
        isWrittenOffOrCancelled
    );
  }, 0);
}

export interface DocumentsFilterJobOption {
  id: string;
  name: string;
}

export interface DocumentsFilterAccountOption {
  id: string;
  name: string;
}

export interface AccountingDocumentsFilters extends CrudListFilters {
  dateFrom: string;
  dateTo: string;
  dueDateFrom: string;
  dueDateTo: string;
  docType: string;
  targetType: string;
  reminderStatus: string;
  status: string;
  cancellationStatus: string;
  search: string;
  cancellationDateFrom: string;
  cancellationDateTo: string;
  selectedJobs: DocumentsFilterJobOption[];
  selectedRecipients: DocumentsFilterAccountOption[];
  skipPersist: boolean;
  paymentReminderBlocked: boolean;
}
export const moduleName = 'accountingDocuments';

@Module({ name: moduleName, namespaced: true })
export class AccountingDocumentsModule extends CrudList<AccountingDocument> {
  crudEndpoint: string = '/documents-api/accounting/documents';
  pagination = {
    skipPersist: false,
    totalRows: 0,
    perPage: 25,
    currentPage: 1,
    currentPageCount: 0,
    numberOfPages: 1,
    sortBy: 'issueDate',
    sortDir: true,
  };
  filters: AccountingDocumentsFilters = {
    skipPersist: false,
    dateFrom: moment().startOf('month').toISOString(),
    dateTo: moment().endOf('day').toISOString(),
    dueDateFrom: '',
    dueDateTo: '',
    docType: '',
    targetType: '',
    reminderStatus: '',
    status: '',
    cancellationStatus: '',
    search: '',
    cancellationDateFrom: '',
    cancellationDateTo: '',
    selectedJobs: [],
    selectedRecipients: [],
    paymentReminderBlocked: false,
  };
  store?: {
    getters?: {
      access_token?: string;
    };
  };
  totalAmount: number | null = null;

  @Action
  async [CrudListActionsEnum.ITEMS_FETCH](payload: CrudListFetchPayload) {
    const result = await super[CrudListActionsEnum.ITEMS_FETCH](payload);

    // Hide documents that are effectively closed: written off, or with an
    // active cancellation (PDF generated or further). The pending cancellation
    // state still shows because the cancellation might be aborted.
    const statusFilter = {
      $and: [
        {
          status: {
            [CondOperator.NOT_IN]: [AccountingDocumentStatusEnum.WRITTEN_OFF],
          },
        },
        {
          $or: [
            { cancellationStatus: { [CondOperator.IS_NULL]: true } },
            {
              cancellationStatus: {
                [CondOperator.NOT_IN]: [
                  AccountingDocumentStatusEnum.CREATED,
                  AccountingDocumentStatusEnum.SEND_PENDING,
                  AccountingDocumentStatusEnum.SENT,
                  AccountingDocumentStatusEnum.PAID,
                ],
              },
            },
          ],
        },
      ],
    };

    const defaultSearch = { $and: [statusFilter] };
    const urlParams = new URLSearchParams(this.noPagingQueryString || '');
    const searchParam = urlParams.get('s');

    let mergedSearch;
    try {
      const decoded = searchParam || '';
      const existingSearch = decoded ? JSON.parse(decoded) : {};

      mergedSearch =
        existingSearch?.$and && Array.isArray(existingSearch.$and)
          ? { ...existingSearch, $and: [...existingSearch.$and, statusFilter] }
          : { $and: [existingSearch, statusFilter] };
    } catch {
      mergedSearch = defaultSearch;
    }

    urlParams.set('s', JSON.stringify(mergedSearch));
    const totalAmountQueryString = urlParams.toString();

    const { data } = await Api.get(
      `${this.crudEndpoint}/total-amount?${totalAmountQueryString}`,
      {
        autoCancelScope: this.crudEndpoint,
      },
    );

    this.context.commit('setTotalAmount', Number(data));
    return result;
  }

  @Mutation
  setTotalAmount(payload: number) {
    this.totalAmount = payload;
  }
  // Getters

  get totalAmountForPage() {
    return calculateTotal(this.currentPageItems);
  }
}

export default AccountingDocumentsModule;
